import React, { useState, useEffect } from "react";
import moment from "moment";

const BoekingLogs = ({ boekingId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (boekingId) {
      fetchLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boekingId]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(`/api/admin/boekingen/${boekingId}/logs`, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch logs');
      }
      
      const data = await response.json();
      setLogs(data.results || []);
      setError(null);
    } catch (err) {
      setError("Fout bij het laden van logs");
      console.error("Error fetching logs:", err);
    } finally {
      setLoading(false);
    }
  };

  const getActieIcon = (actie) => {
    switch (actie) {
      case 'create':
        return <i className="fas fa-plus-circle text-success"></i>;
      case 'update_status':
        return <i className="fas fa-edit text-primary"></i>;
      case 'update_personen':
        return <i className="fas fa-users text-info"></i>;
      case 'update_klant':
        return <i className="fas fa-user-edit text-info"></i>;
      case 'betaling_verwerkt':
        return <i className="fas fa-credit-card text-success"></i>;
      case 'terugbetaling':
        return <i className="fas fa-undo text-warning"></i>;
      case 'mail_verstuurd':
        return <i className="fas fa-envelope text-primary"></i>;
      case 'omboeken':
        return <i className="fas fa-calendar-alt text-info"></i>;
      case 'delete':
        return <i className="fas fa-trash text-danger"></i>;
      default:
        return <i className="fas fa-history text-muted"></i>;
    }
  };

  const getActieLabel = (actie) => {
    const labels = {
      'create': 'Aangemaakt',
      'update_status': 'Status gewijzigd',
      'update_personen': 'Personen bijgewerkt',
      'update_klant': 'Klant gewijzigd',
      'betaling_verwerkt': 'Betaling verwerkt',
      'terugbetaling': 'Terugbetaling',
      'mail_verstuurd': 'E-mail verstuurd',
      'omboeken': 'Omgeboekt',
      'delete': 'Verwijderd'
    };
    return labels[actie] || actie;
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-0">
            <i className="fas fa-history me-2"></i>
            Activiteiten Log
          </h5>
        </div>
        <div className="card-body text-center">
          <i className="fas fa-spinner fa-spin"></i> Logs laden...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-0">
            <i className="fas fa-history me-2"></i>
            Activiteiten Log
          </h5>
        </div>
        <div className="card-body">
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        </div>
      </div>
    );
  }

  const visibleLogs = expanded ? logs : logs.slice(0, 5);

  return (
    <div className="card mt-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0">
          <i className="fas fa-history me-2"></i>
          Activiteiten Log ({logs.length})
        </h5>
        {logs.length > 5 && (
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Minder tonen' : `Alle ${logs.length} tonen`}
          </button>
        )}
      </div>
      <div className="card-body">
        {logs.length === 0 ? (
          <div className="text-muted text-center py-3">
            <i className="fas fa-inbox fa-2x mb-2"></i>
            <p>Geen activiteiten gevonden</p>
          </div>
        ) : (
          <div className="timeline">
            {visibleLogs.map((log, index) => (
              <div key={log._id} className={`timeline-item ${index === 0 ? 'timeline-item-recent' : ''}`}>
                <div className="timeline-marker">
                  {getActieIcon(log.actie)}
                </div>
                <div className="timeline-content">
                  <div className="d-flex justify-content-between align-items-start mb-1">
                    <h6 className="mb-0">{getActieLabel(log.actie)}</h6>
                    <small className="text-muted">
                      {moment(log.timestamp).format('DD-MM-YYYY HH:mm')}
                    </small>
                  </div>
                  <p className="mb-1">{log.beschrijving}</p>
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      <i className="fas fa-user me-1"></i>
                      {log.user_naam}
                    </small>
                    {log.ip_adres && (
                      <small className="text-muted">
                        <i className="fas fa-map-marker-alt me-1"></i>
                        {log.ip_adres}
                      </small>
                    )}
                  </div>
                  
                  {/* Show oude/nieuwe waarde if available */}
                  {(log.oude_waarde || log.nieuwe_waarde) && (
                    <div className="mt-2">
                      <details className="details-custom">
                        <summary className="text-muted small" style={{cursor: 'pointer'}}>
                          <i className="fas fa-eye me-1"></i>
                          Details tonen
                        </summary>
                        <div className="mt-2 p-2 bg-light rounded small">
                          {log.oude_waarde && (
                            <div className="mb-2">
                              <strong>Voor:</strong>
                              <pre className="mb-0">{JSON.stringify(log.oude_waarde, null, 2)}</pre>
                            </div>
                          )}
                          {log.nieuwe_waarde && (
                            <div>
                              <strong>Na:</strong>
                              <pre className="mb-0">{JSON.stringify(log.nieuwe_waarde, null, 2)}</pre>
                            </div>
                          )}
                        </div>
                      </details>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BoekingLogs;